import {useEffect, useMemo, useRef, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {type IAsset} from '@netx/core-asset-next/schema';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import useDispatcher from '@netx/core-components/hooks/useDispatcher';
import {useUpdateEffect} from '@netx/core-components/hooks/useUpdateEffect';
import {AssetViewEvent} from '../actions/constants';
import {sortByDate, sortByString} from '@netx/core-next/utils';
import {
	useGetViewObjectsForAssetReactQuery,
	useReactQueryDeleteView,
	GetViewObjectsForAssetReactQueryKey,
} from '../api';
import {
	type IAssetView,
	AssetViewsSort,
	getAssetViewsDefaultSort,
} from '../schema';

const DEFAULT_DATA: IAssetView[] = [];

const clientSideSort = (data: IAssetView[], sort: AssetViewsSort) => {
	return data.sort((a, b) => {
		switch (sort.field) {
			case 'creationDate':
				return sortByDate(a[sort.field], b[sort.field], sort.order);
			case 'fileSize':
				return (
					(a[sort.field] - b[sort.field]) * (sort.order === 'asc' ? 1 : -1)
				);
			case 'description':
			case 'fileTypeLabel':
			case 'name':
			default:
				return sortByString(
					a[sort.field].toString(),
					b[sort.field].toString(),
					sort.order,
				);
		}
	});
};

export type UseAssetViewsParams = {
	asset?: IAsset;
};

export const useAssetViews = ({asset}: UseAssetViewsParams) => {
	const queryClient = useQueryClient();

	const {data, isFetching, isLoading, error} =
		useGetViewObjectsForAssetReactQuery(
			{
				id: asset?.assetId ?? 0,
			},
			{
				enabled: !!asset,
			},
		);

	const [filter, setFilter] = useState('');
	const [sort, setSort] = useState<AssetViewsSort>(getAssetViewsDefaultSort());

	useUpdateEffect(() => {
		setFilter('');
	}, [asset?.assetId]);

	const _data = useMemo(() => {
		if (!data) return DEFAULT_DATA;

		const _data = clientSideSort([...data], sort);

		if (filter === '') return _data;

		return _data.filter(
			(view) =>
				view.name.toLowerCase().includes(filter.toLowerCase()) ||
				view.description.toLowerCase().includes(filter.toLowerCase()) ||
				view.fileTypeLabel.toLowerCase().includes(filter.toLowerCase()),
		);
	}, [data, filter, sort]);

	const deleteMutation = useReactQueryDeleteView();

	const deleteView = useConstCallback(async (viewId: number) => {
		const view = data?.find((v) => v.viewId === viewId);
		if (!view) return -1;
		const result = await deleteMutation.mutateAsync({id: viewId});
		queryClient.invalidateQueries({
			queryKey: [GetViewObjectsForAssetReactQueryKey, {id: view.imageId}],
		});
		return result;
	});

	const downloadView = useConstCallback(async (viewId: number) => {
		const view = data?.find((v) => v.viewId === viewId);
		if (!view) return;
		dispatcher.trigger('asset:views:download', view);
	});

	const editView = useConstCallback(async (viewId: number) => {});

	useDispatcher(`${AssetViewEvent.EDIT}:succeeded`, (assetId: number) => {
		queryClient.invalidateQueries({
			queryKey: [GetViewObjectsForAssetReactQueryKey, {id: assetId}],
		});
	});
	useDispatcher(
		`${AssetViewEvent.UPLOAD}:succeeded`,
		(assetId: number, assetView: IAssetView) => {
			queryClient.invalidateQueries({
				queryKey: [GetViewObjectsForAssetReactQueryKey, {id: assetId}],
			});
		},
	);

	const _isLoading = !asset || isLoading || isFetching;

	const dataReady = !_isLoading && !!data;

	const filtered = filter !== '';

	return {
		asset,
		data: _data,
		dataReady,
		loading: _isLoading,
		error,
		total: _data.length,
		noRecords: dataReady && _data.length === 0 && !filtered,
		noMatches: dataReady && _data.length === 0 && filtered,
		filtered,
		filter,
		sort,
		setFilter,
		setSort,

		deleteView,
		downloadView,
		editView,
	};
};
